import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Switch } from "../../../client/components/ui/switch";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData, type LiteAccount, type LiteFirm } from "../../data/LiteDataProvider";
import { cushionTone } from "../../ui/AccountCard";
import { Bar, Card, Empty, FirmMark, Pnl, StatusBadge } from "../../ui/ui";
import { RoiText } from "./shared";

export interface FirmRow {
  firm: LiteFirm;
  total: number;
  active: number;
  passRate: number | null;
  spent: number;
  payouts: number;
  net: number;
  roi: number | null;
}

const th = "font-normal px-3 py-2";

/** Récapitulatif par prop firm, depuis le début. */
export function FirmTable({ rows, globalCharges }: { rows: FirmRow[]; globalCharges: number }) {
  const t = useTranslation().lite.pfm.firms;
  const { fmt } = useLiteData();
  return (
    <Card title={t.title} subtitle={t.subtitle} pad={false}>
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
              <th className={`${th} text-left pl-5`}>{t.firm}</th>
              <th className={`${th} text-right`}>{t.accounts}</th>
              <th className={`${th} text-right`}>{t.passRate}</th>
              <th className={`${th} text-right`}>{t.spent}</th>
              <th className={`${th} text-right`}>{t.payouts}</th>
              <th className={`${th} text-right`}>{t.net}</th>
              <th className={`${th} text-right pr-5`}>{t.roi}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(f => (
              <tr key={f.firm.key}>
                <td className="pl-5 px-3 py-3"><span className="inline-flex items-center gap-2.5"><FirmMark firm={f.firm} size="sm" /><span className="font-medium">{f.firm.name}</span></span></td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-muted-foreground">{t.accountsValue(f.active, f.total)}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-muted-foreground">{f.passRate === null ? "-" : fmt.pct(f.passRate)}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{fmt.money(-f.spent)}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-pnlPositive">{fmt.money(f.payouts)}</td>
                <td className="px-3 py-3 text-right"><Pnl value={f.net} className="font-medium" /></td>
                <td className="pr-5 px-3 py-3 text-right"><RoiText roi={f.roi} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {globalCharges > 0 && <p className="px-5 py-3 text-xs text-muted-foreground border-t border-border">{t.globalNote(fmt.money(-globalCharges))}</p>}
    </Card>
  );
}

/** Suivi par compte : état issu du moteur prop firm, coûts et payouts du compte. */
const SHOW_BREACHED_KEY = "edgely:lite:pfm:showBreached";

/** Compte terminé, masqué par le toggle « Afficher les comptes cramés ». */
export const isClosedLiteAccount = (a: Pick<LiteAccount, "status">) => a.status === "breached" || a.status === "inactive";

export function AccountTable({ accounts: all }: { accounts: LiteAccount[] }) {
  const t = useTranslation().lite.pfm.tracking;
  const { fmt } = useLiteData();
  const navigate = useNavigate();
  // Préférence d'affichage de ce navigateur : les comptes fermés (cramés OU inactifs)
  // restent visibles par défaut. Le toggle masque les deux : un compte inactif est
  // tout aussi terminé qu'un compte cramé, seule la cause diffère.
  const [showBreached, setShowBreached] = useState(() => {
    try { return localStorage.getItem(SHOW_BREACHED_KEY) !== "0"; } catch { return true; }
  });
  const toggle = (v: boolean) => {
    setShowBreached(v);
    try { localStorage.setItem(SHOW_BREACHED_KEY, v ? "1" : "0"); } catch { /* stockage indisponible */ }
  };
  const closedCount = all.filter(isClosedLiteAccount).length;
  const accounts = showBreached ? all : all.filter(a => !isClosedLiteAccount(a));
  return (
    <Card
      title={t.title}
      subtitle={t.subtitle}
      pad={false}
      action={closedCount > 0 ? (
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap">
          <Switch checked={showBreached} onCheckedChange={toggle} aria-label={t.showBreached(closedCount)} />
          {t.showBreached(closedCount)}
        </label>
      ) : undefined}
    >
      {accounts.length ? (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
                <th className={`${th} text-left pl-5`}>{t.account}</th>
                <th className={`${th} text-left`}>{t.status}</th>
                <th className={`${th} text-right`}>{t.balance}</th>
                <th className={`${th} text-left w-[150px]`}>{t.target}</th>
                <th className={`${th} text-left w-[150px]`}>{t.cushion}</th>
                <th className={`${th} text-right`}>{t.spent}</th>
                <th className={`${th} text-right`}>{t.payouts}</th>
                <th className={`${th} text-right pr-5`}>{t.net}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {accounts.map(a => (
                <tr key={a.id} onClick={() => navigate(`/account/${a.id}`)} className={`cursor-pointer hover:bg-muted/60 transition-colors ${a.active ? "" : "opacity-60"}`}>
                  <td className="pl-5 px-3 py-3">
                    <span className="inline-flex items-center gap-2.5 min-w-0">
                      <FirmMark firm={a.firm} size="sm" />
                      <span className="font-medium truncate max-w-[220px]">{a.name}</span>
                    </span>
                  </td>
                  <td className="px-3 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums">{a.active ? fmt.money(a.balance, { sign: false }) : "-"}</td>
                  <td className="px-3 py-3">
                    {a.target && a.active ? (
                      <div>
                        <div className="text-[11px] text-muted-foreground font-mono tabular-nums mb-1">{fmt.pct(a.target.ratio)}</div>
                        <Bar value={a.target.ratio} />
                      </div>
                    ) : <span className="text-muted-foreground/70">-</span>}
                  </td>
                  <td className="px-3 py-3">
                    {a.cushion && a.active ? (
                      <div>
                        <div className="text-[11px] text-muted-foreground font-mono tabular-nums mb-1">{fmt.money(a.cushion.remaining, { sign: false })}</div>
                        <Bar value={a.cushion.ratio} tone={cushionTone(a.cushion)} />
                      </div>
                    ) : <span className="text-muted-foreground/70">-</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums">{fmt.money(-a.expensesTotal)}</td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums text-pnlPositive">{fmt.money(a.payoutsTotal)}</td>
                  <td className="pr-5 px-3 py-3 text-right"><Pnl value={a.net} className="font-medium" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty title={t.none} />}
    </Card>
  );
}
